import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Upload, Row, Col } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { clinicService, uploadService } from '../../services';
import { useApp } from '../../context/AppContext';

const ClinicProfile = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { clinic, updateClinic } = useApp();

    useEffect(() => {
        if (clinic) {
            form.setFieldsValue(clinic);
        }
    }, [clinic, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await clinicService.updateProfile(values);
            if (response.success) {
                message.success('Clinic profile updated successfully');
                updateClinic(response.data);
            }
        } catch (error) {
            message.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async ({ file }) => {
        try {
            const response = await uploadService.uploadLogo(file);
            if (response.success) {
                message.success('Logo uploaded successfully');
                const updated = await clinicService.getProfile();
                updateClinic(updated.data);
            }
        } catch (error) {
            message.error('Failed to upload logo');
        }
    };

    const handleSignatureUpload = async ({ file }) => {
        try {
            const response = await uploadService.uploadSignature(file);
            if (response.success) {
                message.success('Digital signature uploaded successfully');
                const updated = await clinicService.getProfile();
                updateClinic(updated.data);
            }
        } catch (error) {
            message.error('Failed to upload signature');
        }
    };

    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>Clinic Profile</h1>
            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="clinicName" label="Clinic Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="ownerName" label="Owner Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="phone" label="Phone Number" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="website" label="Website">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name={['address', 'street']} label="Street Address">
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name={['address', 'city']} label="City">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={['address', 'state']} label="State">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={['address', 'zipCode']} label="Zip Code">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Clinic Logo">
                                <Upload
                                    customRequest={handleLogoUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />}>Upload Logo</Button>
                                </Upload>
                                {clinic?.logo && (
                                    <img
                                        src={clinic.logo.startsWith('http') ? clinic.logo : `${process.env.REACT_APP_API_URL}${clinic.logo}`}
                                        alt="Logo"
                                        style={{ marginTop: 10, maxHeight: 100 }}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Digital Signature">
                                <Upload
                                    customRequest={handleSignatureUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />}>Upload Signature</Button>
                                </Upload>
                                {clinic?.digitalSignature && (
                                    <img
                                        src={clinic.digitalSignature.startsWith('http') ? clinic.digitalSignature : `${process.env.REACT_APP_API_URL}${clinic.digitalSignature}`}
                                        alt="Signature"
                                        style={{ marginTop: 10, maxHeight: 100 }}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Update Profile
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ClinicProfile;
